export type PopupTrigger = { kind: 'delay'; seconds: number } | { kind: 'exit' } | { kind: 'scroll'; percent: number };

export interface PopupRules {
  formId: string;
  /** Days before the pop-up may open again for this visitor. */
  periodDays: number;
  trigger: PopupTrigger;
}

const DAY_MS = 86_400_000;

const memoryKey = (formId: string): string => `prodantix-form-popup:${formId}`;

const readOpened = (formId: string): number | null => {
  try {
    const stored = window.localStorage.getItem(memoryKey(formId));
    return stored === null ? null : Number(stored);
  } catch {
    return null;
  }
};

const rememberOpened = (formId: string, at: number): void => {
  try {
    window.localStorage.setItem(memoryKey(formId), String(at));
  } catch {
    return;
  }
};

/** Whether the pop-up may open now, given when it last opened. */
export const isDue = (rules: PopupRules, now: number, lastOpened: number | null): boolean => lastOpened === null || now - lastOpened >= rules.periodDays * DAY_MS;

/** Opens the pop-up once its trigger fires, once per visitor per period, and
 *  answers a function that stops waiting. */
export const watchPopup = (rules: PopupRules, open: () => void, now: () => number = Date.now): (() => void) => {
  if (!isDue(rules, now(), readOpened(rules.formId))) {
    return () => undefined;
  }
  let done = false;
  const cleanups: (() => void)[] = [];
  const fire = (): void => {
    if (done) {
      return;
    }
    done = true;
    for (const cleanup of cleanups) {
      cleanup();
    }
    rememberOpened(rules.formId, now());
    open();
  };
  const trigger = rules.trigger;
  if (trigger.kind === 'delay') {
    const timer = setTimeout(fire, trigger.seconds * 1000);
    cleanups.push(() => clearTimeout(timer));
  } else if (trigger.kind === 'scroll') {
    const onScroll = (): void => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0 || (window.scrollY / scrollable) * 100 >= trigger.percent) {
        fire();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));
  } else {
    const onLeave = (event: MouseEvent): void => {
      if (event.relatedTarget === null && event.clientY <= 0) {
        fire();
      }
    };
    document.addEventListener('mouseout', onLeave);
    cleanups.push(() => document.removeEventListener('mouseout', onLeave));
  }
  return () => {
    done = true;
    for (const cleanup of cleanups) {
      cleanup();
    }
  };
};

/** Parses a script tag's `data-popup`: `exit`, `delay:5` or `scroll:50`. */
export const parseTrigger = (value: string | null | undefined): PopupTrigger | null => {
  if (!value) {
    return null;
  }
  if (value === 'exit') {
    return { kind: 'exit' };
  }
  const [kind, amount] = value.split(':');
  const number = Number(amount);
  if (!Number.isFinite(number) || number < 0) {
    return null;
  }
  if (kind === 'delay') {
    return { kind: 'delay', seconds: number };
  }
  if (kind === 'scroll') {
    return { kind: 'scroll', percent: Math.min(100, number) };
  }
  return null;
};
